/**
 * use-referral-queue.ts — React hook for fetching the referral queue.
 *
 * Fetches POST /admin/referrals/search with the given status filter.
 * Status change triggers a new server fetch; name/NPI search is client-side.
 *
 * Auth: uses auth.user?.access_token from @nrx/auth-ui. On 401: calls
 * auth.signoutRedirect().
 */

import { usePortalAuth } from '@nrx/auth-ui';
import { useState, useEffect, useCallback } from 'react';

import { referralApi } from './referral-api.js';
import type { PendingReferralSummary, ReferralQueueStatus } from './referral-api.js';

export interface UseReferralQueueReturn {
  items: PendingReferralSummary[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

export function useReferralQueue(
  status: ReferralQueueStatus = 'awaiting_provider_review',
): UseReferralQueueReturn {
  const auth = usePortalAuth();
  const [items, setItems] = useState<PendingReferralSummary[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchQueue = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      const token = auth.user?.access_token;
      const result = await referralApi.searchReferrals({ status }, token);
      setItems(result.items);
    } catch (err) {
      if (err instanceof Error && err.message.includes('401')) {
        void auth.signoutRedirect();
        return;
      }
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setIsLoading(false);
    }
  }, [status, auth]);

  useEffect(() => {
    void fetchQueue();
  }, [fetchQueue]);

  return {
    items,
    isLoading,
    error,
    refetch: (): void => {
      void fetchQueue();
    },
  };
}
